import React, { useState, useEffect } from 'react';

interface ActiveEffect {
  id: string;
  name: string;
  description: string;
  duration: number;
  remainingTime: number;
  value?: number;
  stacks?: number;
  icon?: string;
  startTime: number;
  effectType?: 'buff' | 'debuff' | 'proc';
}

interface EffectBarProps {
  effects: { [effectId: string]: ActiveEffect };
}

const EffectIcon: React.FC<{ effect: ActiveEffect }> = ({ effect }) => {
  const [displayTime, setDisplayTime] = useState(Math.ceil(effect.remainingTime));

  useEffect(() => {
    const newDisplayTime = Math.ceil(effect.remainingTime);
    if (newDisplayTime !== displayTime) {
      setDisplayTime(newDisplayTime);
    }
  }, [effect.remainingTime, displayTime]);

  const getEffectColor = (effect: ActiveEffect) => {
    // 使用effectType进行分类，如果没有则根据描述推断
    switch (effect.effectType) {
      case 'proc':
        return '#4CAF50'; // 绿色 - Proc效果
      case 'buff':
        return '#2196F3'; // 蓝色 - 增益效果
      case 'debuff':
        return '#F44336'; // 红色 - 减益效果
      default:
        // 后备逻辑：根据描述推断
        if (effect.description?.includes('几率') || effect.description?.includes('准备')) {
          return '#4CAF50';
        }
        if (effect.description?.includes('持续伤害') || effect.description?.includes('减少')) {
          return '#F44336';
        }
        return '#2196F3'; // 默认为增益效果
    }
  };

  const progressPercentage = (effect.remainingTime / effect.duration) * 100;

  return (
    <div 
      style={{
        position: 'relative',
        width: '36px',
        height: '36px',
        margin: '1px',
        border: '2px solid #333',
        borderRadius: '4px',
        backgroundColor: getEffectColor(effect),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}
      title={`${effect.name}\n${effect.description}\n剩余: ${effect.remainingTime.toFixed(1)}s`}
    >
      {/* 效果图标/文字 */}
      {effect.icon ? (
        <img 
          src={effect.icon} 
          alt={effect.name}
          style={{
            width: '28px',
            height: '28px',
            borderRadius: '2px',
            zIndex: 2
          }}
          onError={(e) => {
            // 如果图片加载失败，显示文字
            e.currentTarget.style.display = 'none';
            const textElement = e.currentTarget.nextElementSibling as HTMLElement;
            if (textElement) {
              textElement.style.display = 'flex';
            }
          }}
        />
      ) : null}
      <div style={{
        color: 'white',
        fontSize: '10px',
        fontWeight: 'bold',
        textAlign: 'center',
        zIndex: 2,
        display: effect.icon ? 'none' : 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%'
      }}>
        {effect.name.substring(0, 2)}
      </div>

      {/* 时间进度条 */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: `${progressPercentage}%`,
        height: '4px',
        backgroundColor: 'rgba(255, 255, 255, 0.8)',
        transition: 'width 0.1s linear'
      }} />

      {/* 剩余时间显示 */}
      <div style={{
        position: 'absolute',
        bottom: '6px',
        right: '2px',
        color: 'white',
        fontSize: '8px',
        fontWeight: 'bold',
        textShadow: '1px 1px 1px rgba(0,0,0,0.8)',
        zIndex: 10
      }}>
        {displayTime}
      </div>
    </div>
  );
};

export const EffectBar: React.FC<EffectBarProps> = ({ effects }) => {
  const effectList = Object.values(effects).filter(effect => effect.remainingTime > 0);

  if (effectList.length === 0) {
    return null;
  }

  // 根据effectType分离 buff 和 debuff
  const buffs = effectList.filter(effect => 
    effect.effectType === 'buff' || effect.effectType === 'proc' ||
    (!effect.effectType && !effect.description?.includes('持续伤害'))
  );
  const debuffs = effectList.filter(effect => 
    effect.effectType === 'debuff' ||
    (!effect.effectType && effect.description?.includes('持续伤害'))
  );

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      padding: '6px 8px',
      backgroundColor: 'rgba(42, 42, 42, 0.9)',
      borderRadius: '4px',
      border: '1px solid rgba(68, 68, 68, 0.8)',
      alignItems: 'center'
    }}>
      {/* Buff 行 */}
      {buffs.length > 0 && (
        <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
          <div style={{ color: '#4CAF50', fontSize: '9px', marginRight: '4px' }}>
            增益:
          </div>
          {buffs.map(effect => (
            <EffectIcon key={effect.id} effect={effect} />
          ))}
        </div>
      )}
      
      {/* Debuff 行 */}
      {debuffs.length > 0 && (
        <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
          <div style={{ color: '#F44336', fontSize: '9px', marginRight: '4px' }}>
            减益:
          </div>
          {debuffs.map(effect => (
            <EffectIcon key={effect.id} effect={effect} />
          ))}
        </div>
      )}
    </div>
  );
};